import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Badge, Card } from '@/design-system/ui/Card';
import { Button } from '@/design-system/ui/Button';
import { Icon } from '@/design-system/ui/Icon';
import { Disclosure } from '@/design-system/ui/Disclosure';
import { EmptyState } from '@/design-system/ui/States';
import { toast } from '@/design-system/ui/toast';
import { SITE_NAME, SITE_URL } from '@/core/config/content';
import { useSEO } from '@/core/seo/useSEO';
import { articles } from '../data/magazine';

export default function MagazineArticlePage() {
  const { slug } = useParams();
  const article = articles.find((a) => a.slug === slug);
  const related = article ? articles.filter((a) => a.id !== article.id).slice(0, 3) : [];

  const [readingProgress, setReadingProgress] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      const el = document.documentElement;
      const total = el.scrollHeight - el.clientHeight;
      if (total > 0) {
        setReadingProgress(Math.min(100, Math.max(0, (window.scrollY / total) * 100)));
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleCopyLink = () => {
    if (typeof window !== 'undefined') {
      navigator.clipboard.writeText(window.location.href);
      toast.success('لینک مقاله با موفقیت کپی شد!');
    }
  };

  const articleUrl = article ? `${SITE_URL}/magazine/${article.slug}` : SITE_URL;
  const shareText = article ? `${article.title}\nدر مجله بلینو بخوانید:\n` : '';

  useSEO({
    title: article ? `${article.title} | مجله بلینو` : 'مقاله یافت نشد',
    description: article ? article.excerpt : 'مقاله موردنظر در مجله بلینو یافت نشد.',
    canonical: article ? `/magazine/${article.slug}` : '/magazine',
    keywords: article?.keywords?.join(', ') || 'مجله بلینو, تاکسی بین شهری کرمان, سفر بین شهری',
    ...(article && {
      jsonLd: {
        '@context': 'https://schema.org',
        '@graph': [
          {
            '@type': 'Article',
            headline: article.title,
            description: article.excerpt,
            articleSection: article.category,
            datePublished: '2026-09-01',
            dateModified: '2026-10-28',
            inLanguage: 'fa-IR',
            ...(article.keywords ? { keywords: article.keywords.join(', ') } : {}),
            author: { '@type': 'Organization', name: SITE_NAME, url: SITE_URL },
            publisher: {
              '@type': 'Organization',
              name: SITE_NAME,
              url: SITE_URL,
              logo: { '@type': 'ImageObject', url: `${SITE_URL}/assets/favicon-D-SbSG2n.svg` },
            },
            mainEntityOfPage: articleUrl,
          },
          {
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: SITE_NAME, item: `${SITE_URL}/` },
              { '@type': 'ListItem', position: 2, name: 'مجله بلینو', item: `${SITE_URL}/magazine` },
              { '@type': 'ListItem', position: 3, name: article.title, item: articleUrl },
            ],
          },
          ...(article.faqs && article.faqs.length > 0
            ? [
                {
                  '@type': 'FAQPage',
                  mainEntity: article.faqs.map((f) => ({
                    '@type': 'Question',
                    name: f.q,
                    acceptedAnswer: { '@type': 'Answer', text: f.a },
                  })),
                },
              ]
            : []),
        ],
      },
    }),
  });

  if (!article) {
    return (
      <EmptyState
        title="این مقاله پیدا نشد"
        description="ممکن است این مقاله حذف شده یا آدرس آن اشتباه باشد."
        action={
          <Link to="/magazine">
            <Button>بازگشت به مجله بلینو</Button>
          </Link>
        }
      />
    );
  }

  const headings = article.sections
    .map((s, idx) => ({ heading: s.heading, id: `section-${idx}` }))
    .filter((h): h is { heading: string; id: string } => Boolean(h.heading));

  return (
    <>
      {/* Top Reading Progress Bar */}
      <div
        className="fixed inset-x-0 top-0 z-50 h-1 bg-gradient-to-r from-action via-mint to-cta transition-all duration-150"
        style={{ width: `${readingProgress}%` }}
        aria-hidden
      />

      <article className="mx-auto max-w-3xl px-4 py-8 md:py-12">
        {/* Breadcrumb Navigation */}
        <nav aria-label="مسیر صفحه" className="mb-6 flex flex-wrap items-center gap-2 text-xs text-ink-3">
          <Link to="/" className="hover:text-action">
            خانه
          </Link>
          <Icon name="chevronLeft" size={12} className="opacity-60" />
          <Link to="/magazine" className="hover:text-action">
            مجله بلینو
          </Link>
          <Icon name="chevronLeft" size={12} className="opacity-60" />
          <span className="text-ink-2">{article.category}</span>
          <Icon name="chevronLeft" size={12} className="opacity-60" />
          <span className="truncate font-medium text-ink max-w-[200px] sm:max-w-[320px]">
            {article.title}
          </span>
        </nav>

        {/* Article Header */}
        <header className="mb-8">
          <div className="mb-3 flex flex-wrap items-center gap-2">
            <Badge tone="info" className="text-xs font-bold">
              {article.category}
            </Badge>
            {article.featured && (
              <Badge tone="warning" className="text-xs font-bold">
                ★ مقاله برگزیده
              </Badge>
            )}
          </div>

          <h1 className="text-2xl font-bold leading-relaxed text-ink sm:text-3xl md:text-4xl md:leading-snug">
            {article.title}
          </h1>

          {/* Editorial Meta Bar */}
          <div className="mt-5 flex flex-wrap items-center justify-between gap-4 border-y border-line py-3 text-xs text-ink-2">
            <div className="flex items-center gap-3">
              <span className="inline-flex items-center gap-1 font-bold text-navy">
                <Icon name="verified" size={16} className="text-action" />
                تیم تحریریه و ترابری بلینو
              </span>
              <span>·</span>
              <span className="inline-flex items-center gap-1">
                <Icon name="clock" size={14} />
                {article.readTime} مطالعه
              </span>
              <span>·</span>
              <span>{article.publishedAt}</span>
            </div>

            {/* Quick Share Buttons */}
            <div className="flex items-center gap-2">
              <a
                href={`https://api.whatsapp.com/send?text=${encodeURIComponent(shareText + articleUrl)}`}
                target="_blank"
                rel="noopener noreferrer"
                title="اشتراک‌گذاری در واتساپ"
                aria-label="اشتراک‌گذاری در واتساپ"
                className="grid size-8 place-items-center rounded-full bg-mint/15 text-navy transition hover:bg-mint hover:text-white"
              >
                <Icon name="whatsapp" size={16} />
              </a>
              <a
                href={`https://t.me/share/url?url=${encodeURIComponent(articleUrl)}&text=${encodeURIComponent(article.title)}`}
                target="_blank"
                rel="noopener noreferrer"
                title="اشتراک‌گذاری در تلگرام"
                aria-label="اشتراک‌گذاری در تلگرام"
                className="grid size-8 place-items-center rounded-full bg-primary-light text-navy transition hover:bg-solid hover:text-white"
              >
                <Icon name="telegram" size={16} />
              </a>
              <button
                type="button"
                onClick={handleCopyLink}
                title="کپی لینک مقاله"
                aria-label="کپی لینک مقاله"
                className="grid size-8 place-items-center rounded-full bg-fill text-ink-2 transition hover:bg-paper hover:text-action hover:shadow-sm"
              >
                <Icon name="copy" size={16} />
              </button>
            </div>
          </div>
        </header>

        {/* Key Takeaways Box (AEO & Fast Reader Highlights) */}
        {article.keyTakeaways && article.keyTakeaways.length > 0 && (
          <aside
            aria-label="نکات کلیدی مقاله"
            className="mb-8 rounded-2xl border-2 border-action/20 bg-gradient-to-br from-primary-light/60 via-paper to-primary-light/40 p-5 shadow-sm sm:p-6"
          >
            <div className="flex items-center gap-2 font-bold text-navy">
              <span className="grid size-7 place-items-center rounded-lg bg-action/15 text-action">
                <Icon name="sparkles" size={18} />
              </span>
              <span>نکات کلیدی این راهنما</span>
            </div>
            <ul className="mt-3.5 space-y-2 text-xs leading-relaxed text-ink-2 sm:text-sm">
              {article.keyTakeaways.map((point, i) => (
                <li key={i} className="flex items-start gap-2.5">
                  <span className="mt-1 size-1.5 shrink-0 rounded-full bg-action" />
                  <span>{point}</span>
                </li>
              ))}
            </ul>
          </aside>
        )}

        {/* Table of Contents */}
        {headings.length > 1 && (
          <nav
            aria-label="فهرست مطالب"
            className="mb-9 rounded-2xl border border-line bg-fill/50 p-4 sm:p-5"
          >
            <div className="flex items-center gap-2 font-bold text-ink">
              <Icon name="list" size={18} className="text-action" />
              <span>فهرست بخش‌های راهنما</span>
            </div>
            <ol className="mt-3 space-y-2 text-xs text-ink-2 sm:text-sm">
              {headings.map((h, i) => (
                <li key={h.id}>
                  <a
                    href={`#${h.id}`}
                    onClick={(e) => {
                      e.preventDefault();
                      document.getElementById(h.id)?.scrollIntoView({ behavior: 'smooth' });
                    }}
                    className="inline-flex items-center gap-2 font-medium text-ink-2 transition hover:text-action hover:underline"
                  >
                    <span className="text-[11px] font-bold text-action/70">{i + 1}.</span>
                    <span>{h.heading}</span>
                  </a>
                </li>
              ))}
            </ol>
          </nav>
        )}

        {/* Main Content Sections */}
        <div className="flex flex-col gap-8">
          {article.sections.map((s, idx) => (
            <section key={idx} id={s.heading ? `section-${idx}` : undefined} className="scroll-mt-20">
              {s.heading && (
                <h2 className="mb-3.5 flex items-center gap-2.5 border-s-4 border-action ps-3 text-lg font-bold text-ink sm:text-xl">
                  {s.heading}
                </h2>
              )}

              <p className="whitespace-pre-line text-justify text-sm leading-[2.2] text-ink-2 sm:text-base sm:leading-[2.3]">
                {s.body}
              </p>

              {/* Responsive Data Table */}
              {s.table && (
                <div className="my-5 overflow-x-auto rounded-xl border border-line bg-paper shadow-sm">
                  <table className="w-full text-right text-xs sm:text-sm">
                    <thead className="border-b border-line bg-fill text-ink">
                      <tr>
                        {s.table.headers.map((h, i) => (
                          <th key={i} className="px-4 py-3 font-bold">
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-line/60">
                      {s.table.rows.map((row, rIdx) => (
                        <tr key={rIdx} className="transition-colors hover:bg-fill/40">
                          {row.map((cell, cIdx) => (
                            <td key={cIdx} className="px-4 py-3 text-ink-2">
                              {cell}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Callout Box */}
              {s.callout && (
                <div className="my-4 rounded-xl border-s-4 border-mint bg-mint/10 p-4 text-xs leading-relaxed text-ink-2 sm:text-sm">
                  <strong className="block font-bold text-navy">{s.callout.title}</strong>
                  <p className="mt-1">{s.callout.text}</p>
                </div>
              )}
            </section>
          ))}
        </div>

        {/* In-Article Route Booking CTA Card */}
        {article.targetRoute && (
          <aside className="my-10 overflow-hidden rounded-2xl border-2 border-action bg-gradient-to-r from-hero via-navy-deep to-hero p-6 text-white shadow-floating">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <span className="inline-block rounded-full bg-mint/20 px-3 py-1 text-xs font-bold text-mint">
                  رزرو آنلاین سواری بین‌شهری
                </span>
                <h3 className="mt-2 text-lg font-bold sm:text-xl">
                  رزرو تاکسی بین‌شهری {article.targetRoute.label}
                </h3>
                <p className="mt-1 text-xs text-white/80">
                  صندلی خود را آنلاین انتخاب کنید و بدون معطلی در ایستگاه حرکت کنید.
                </p>
              </div>
              <Link
                to={`/trips?origin=${encodeURIComponent(article.targetRoute.origin)}&destination=${encodeURIComponent(article.targetRoute.destination)}`}
                className="shrink-0"
              >
                <Button size="lg" className="w-full bg-cta text-white hover:brightness-110 sm:w-auto">
                  مشاهده و رزرو حرکت‌های امروز
                </Button>
              </Link>
            </div>
          </aside>
        )}

        {/* Interactive FAQ Accordion for AEO / Google PAA */}
        {article.faqs && article.faqs.length > 0 && (
          <section className="my-10 border-t border-line pt-8" aria-labelledby="faq-heading">
            <h2 id="faq-heading" className="mb-4 text-lg font-bold text-ink sm:text-xl">
              سوالات پرتکرار مسافران
            </h2>
            <div className="flex flex-col gap-3">
              {article.faqs.map((faq, i) => (
                <Disclosure key={i} summary={faq.q} defaultOpen={i === 0}>
                  <p className="text-sm leading-relaxed text-ink-2">{faq.a}</p>
                </Disclosure>
              ))}
            </div>
          </section>
        )}

        {/* Share & Copy Footer Bar */}
        <div className="my-8 flex flex-wrap items-center justify-between gap-4 rounded-xl border border-line bg-paper p-4">
          <span className="text-xs font-bold text-ink">این راهنما را برای دوستان و همراهان سفر خود بفرستید:</span>
          <div className="flex items-center gap-2">
            <a
              href={`https://api.whatsapp.com/send?text=${encodeURIComponent(shareText + articleUrl)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 rounded-lg bg-mint/15 px-3 py-1.5 text-xs font-bold text-navy hover:bg-mint hover:text-white"
            >
              <Icon name="whatsapp" size={15} />
              واتساپ
            </a>
            <a
              href={`https://t.me/share/url?url=${encodeURIComponent(articleUrl)}&text=${encodeURIComponent(article.title)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 rounded-lg bg-primary-light px-3 py-1.5 text-xs font-bold text-navy hover:bg-solid hover:text-white"
            >
              <Icon name="telegram" size={15} />
              تلگرام
            </a>
            <button
              type="button"
              onClick={handleCopyLink}
              className="inline-flex items-center gap-1.5 rounded-lg border border-line bg-fill px-3 py-1.5 text-xs font-bold text-ink hover:bg-paper"
            >
              <Icon name="copy" size={15} />
              کپی لینک
            </button>
          </div>
        </div>

        {/* Related Articles */}
        {related.length > 0 && (
          <section className="my-10 border-t border-line pt-8" aria-labelledby="related-heading">
            <h2 id="related-heading" className="mb-5 text-lg font-bold text-ink">
              مقالات و راهنماهای مرتبط
            </h2>
            <ul className="grid gap-4 sm:grid-cols-3">
              {related.map((r) => (
                <li key={r.id}>
                  <Link
                    to={`/magazine/${r.slug}`}
                    className="group block h-full rounded-xl border border-line bg-paper p-4 transition-all duration-200 hover:border-action/40 hover:shadow-md"
                  >
                    <div className="mb-2 flex items-center justify-between">
                      <Badge tone="info" className="text-[11px]">
                        {r.category}
                      </Badge>
                      <span className="text-[10px] text-ink-3">{r.readTime}</span>
                    </div>
                    <p className="line-clamp-2 text-xs font-bold leading-relaxed text-ink group-hover:text-action">
                      {r.title}
                    </p>
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* Bottom CTA to Magazine and Trips */}
        <Card className="mt-8 bg-gradient-to-br from-primary-light via-paper to-primary-light p-6 text-center">
          <p className="mb-1 font-bold text-navy">دانشنامه جامع سفر بین‌شهری بلینو</p>
          <p className="mb-4 text-xs text-ink-2">
            راهنمای کامل مسیرها، نرخ‌ها، ایستگاه‌ها و سیستم پیگیری مرسولات
          </p>
          <div className="flex justify-center gap-3">
            <Link to="/magazine">
              <Button variant="outline" size="sm">
                مشاهده همه مقالات مجله
              </Button>
            </Link>
            <Link to="/#search">
              <Button size="sm" icon={<Icon name="search" size={16} />}>
                جستجوی بلیط بین‌شهری
              </Button>
            </Link>
          </div>
        </Card>
      </article>
    </>
  );
}
